/**
 * Left rail. Nothing here names an industry: documents go in, structured data comes
 * out, and everything is grouped by what a person is doing rather than which table
 * backs it. Sections that are planned but unbuilt are shown and marked, because a
 * console that hides its roadmap is indistinguishable from one that has none.
 */
import { NavLink } from "react-router-dom";
import type { ReactNode } from "react";
import { useListQuery } from "../lib/useListQuery";
import type { CollectionRef } from "../lib/types";

const icon = (path: ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="square"
    strokeLinejoin="miter"
    className="size-full"
  >
    {path}
  </svg>
);

function Item({
  to,
  glyph,
  children,
  soon = false,
}: {
  to: string;
  glyph: ReactNode;
  children: ReactNode;
  soon?: boolean;
}) {
  if (soon) {
    return (
      <span
        aria-disabled="true"
        title="Not built yet"
        className="mx-3 flex cursor-not-allowed items-center gap-3 px-2.5 py-1.5 text-[13px] text-faint"
      >
        <span className="size-4 opacity-40">{glyph}</span>
        {children}
        <span className="ms-auto font-mono text-[9px] tracking-[0.18em] uppercase">soon</span>
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `group relative mx-3 flex items-center gap-3 px-2.5 py-1.5 text-[13px] transition-colors duration-200 ease-alt ${
          isActive ? "bg-gold-wash font-semibold text-gold" : "text-dim hover:text-text"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <i className="absolute inset-y-0 start-0 w-[2px] bg-gold" />}
          <span className={`size-4 ${isActive ? "" : "opacity-60"}`}>{glyph}</span>
          {children}
        </>
      )}
    </NavLink>
  );
}

function CollectionSwitcher({
  collections,
}: {
  collections: readonly Pick<CollectionRef, "id" | "name">[];
}) {
  const query = useListQuery();
  const current = query.all("collection");
  return (
    <label className="mx-3 mb-3 flex flex-col gap-1">
      <span className="eyebrow">collection</span>
      <select
        aria-label="collection"
        value={current.length === 1 ? current[0] : ""}
        onChange={(event) =>
          query.set({ collection: event.target.value === "" ? null : event.target.value })
        }
        className="border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
      >
        <option value="">all documents</option>
        {collections.map((collection) => (
          <option key={collection.id} value={collection.id}>
            {collection.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Rail({
  footer,
  can,
  collections,
}: {
  footer: ReactNode;
  /** From `usePermission()`. An item the role cannot use is absent, not disabled. */
  can: (permission: string) => boolean;
  /** Scopes the whole console to one collection. A tag never appears here — a tag is
   *  ambient, and a rail full of them is a rail nobody reads. */
  collections: readonly Pick<CollectionRef, "id" | "name">[];
}) {

  return (
    <aside className="sticky top-0 flex h-screen flex-col overflow-y-auto border-e border-hair bg-ground-soft pb-4">
      <div className="mb-4 flex items-center gap-3 border-b border-hair px-4 py-4">
        <span className="grid size-8 shrink-0 place-items-center bg-gold-surface text-[17px] leading-none font-black text-on-gold select-none">
          a
        </span>
        <div className="min-w-0 leading-none">
          <h1 className="truncate text-[14px] font-extrabold tracking-[0.14em] uppercase">
            alt<span className="text-gold">-</span>parser
          </h1>
          <span className="mt-1 block font-mono text-[9px] tracking-[0.22em] text-faint uppercase">
            document intelligence
          </span>
        </div>
      </div>

      <CollectionSwitcher collections={collections} />

      <p className="eyebrow px-4 pt-2 pb-1.5">Library</p>
      <Item
        to="/collections"
        glyph={icon(
          <>
            <path d="M4 6h7l2 2h7v12H4z" />
            <path d="M4 10h16" />
          </>,
        )}
      >
        Collections
      </Item>
      <Item
        to="/tags"
        glyph={icon(
          <>
            <path d="M4 4h8l8 8-8 8-8-8z" />
            <circle cx="9" cy="9" r="1.4" />
          </>,
        )}
      >
        Tags
      </Item>

      <p className="eyebrow px-4 pt-4 pb-1.5">Intake</p>
      <Item to="/documents" glyph={icon(<><path d="M14 3v5h5" /><path d="M19 8v12H5V4h9z" /></>)}>
        Documents
      </Item>
      <Item to="/jobs" glyph={icon(<><path d="M4 4h16v16H4z" /><path d="M12 8v4l3 2" /></>)}>
        Jobs
      </Item>
      <Item
        to="/search"
        glyph={icon(<><circle cx="11" cy="11" r="6" /><path d="m20 20-3.5-3.5" /></>)}
      >
        Search
      </Item>

      <p className="eyebrow px-4 pt-4 pb-1.5">Extract</p>
      <Item
        to="/templates"
        glyph={icon(
          <>
            <path d="M4 4h16v16H4z" />
            <path d="M4 9h16M9 9v11" />
          </>,
        )}
      >
        Forms
      </Item>
      <Item to="/schemas" soon glyph={icon(<><path d="M4 6h16M4 12h10M4 18h13" /></>)}>
        Schemas
      </Item>
      <Item to="/workflows" soon glyph={icon(<><path d="M4 5h5v5H4zM15 14h5v5h-5zM9 7.5h6v9" /></>)}>
        Workflows
      </Item>

      <p className="eyebrow px-4 pt-4 pb-1.5">Verify</p>
      <Item to="/review" glyph={icon(<><path d="M12 3l8 3v6c0 5-3.5 7.6-8 9-4.5-1.4-8-4-8-9V6z" /><path d="m9 12 2 2 4-4" /></>)}>
        Review queue
      </Item>

      <p className="eyebrow px-4 pt-4 pb-1.5">Deliver</p>
      <Item to="/webhooks" glyph={icon(<><path d="M9 17H6a3 3 0 0 1 0-6h1" /><path d="M15 7h3a3 3 0 0 1 0 6h-1" /><path d="M8 12h8" /></>)}>
        Webhooks
      </Item>

      {(can("users:manage") ||
        can("keys:manage") ||
        can("audit:read") ||
        can("retention:erase")) && (
        <>
          <p className="eyebrow px-4 pt-4 pb-1.5">Settings</p>
          {can("users:manage") && (
            <Item
              to="/settings/users"
              glyph={icon(
                <>
                  <circle cx="9" cy="8" r="3" />
                  <path d="M4 19c0-3 2.2-5 5-5s5 2 5 5" />
                  <path d="M16 11h4M18 9v4" />
                </>,
              )}
            >
              Users
            </Item>
          )}
          {can("keys:manage") && (
            <Item
              to="/settings/keys"
              glyph={icon(
                <>
                  <circle cx="8" cy="12" r="3" />
                  <path d="M11 12h9M17 12v3" />
                </>,
              )}
            >
              API keys
            </Item>
          )}
          {can("audit:read") && (
            <Item
              to="/settings/audit"
              glyph={icon(
                <>
                  <path d="M5 4h11l3 3v13H5z" />
                  <path d="M8 10h8M8 14h5" />
                </>,
              )}
            >
              Audit
            </Item>
          )}
          {can("retention:erase") && (
            <Item
              to="/settings/retention"
              glyph={icon(
                <>
                  <path d="M4 7h16M9 7V5h6v2" />
                  <path d="M6 7v13h12V7" />
                </>,
              )}
            >
              Retention
            </Item>
          )}
        </>
      )}
      <div className="mt-auto flex flex-col gap-2 border-t border-hair px-4 pt-4">{footer}</div>
    </aside>
  );
}

export function RailStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between font-mono text-[10.5px] tracking-[0.14em] uppercase">
      <span className="text-faint">{label}</span>
      <b className="tnum font-bold text-dim">{value}</b>
    </div>
  );
}
